//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { Alert, AlertIcon, Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Granularity } from "../../../api/usage/v1/usage";
import { SectionSpinner } from "../../../components/SectionSpinner";
import { CreditUsageBreakdown } from "./CreditUsageBreakdown";
import { CreditUsageChart } from "./CreditUsageChart";
import { CreditUsageRangeSelector } from "./CreditUsageRangeSelector";
import { lastCompleteDay } from "./lastCompleteDay";
import { ALL_COMPONENTS, UsageComponentFilter } from "./UsageComponentFilter";
import { useFetchCreditUsage } from "./useFetchCreditUsage";
import { clampPeriodToRange, periodBoundsFor, UsageRangeKey, usageRangeFor } from "./usageRange";

export const DeploymentUsageView = ({ deploymentId }: { deploymentId: string }) => {
  const [range, setRange] = useState<UsageRangeKey>("MONTH_1");
  const [component, setComponent] = useState<string>(ALL_COMPONENTS);
  // The period (day, or month for the 1-year range) the viewer clicked on in
  // the chart, if any. undefined means the breakdown below covers the whole
  // selected range, same as before this drill-down existed.
  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(undefined);

  // A clicked day only means anything relative to the component it was
  // clicked under - switching it invalidates the selection, since it may no
  // longer reflect what "all components" vs. one component means. `range` is
  // deliberately NOT a dependency here - see handleRangeChange below for why
  // it cannot go through this effect.
  useEffect(() => {
    setSelectedPeriod(undefined);
  }, [component]);

  // range and selectedPeriod must change together, in the same handler, not
  // via the effect above: chart.granularity below is derived straight from
  // `range` and is already the NEW granularity in the very render where
  // `range` changes, but the effect only clears selectedPeriod AFTER that
  // render commits. That leaves one render where `period` below calls
  // periodBoundsFor(selectedPeriod, chart.granularity) with a label
  // formatted for the OLD granularity (e.g. "2026-08") under the NEW one
  // (e.g. DAY) - label.split("-") then yields an undefined `day`, so
  // periodBoundsFor's Date.UTC(...) produces Invalid Date bounds that flow
  // straight into that render's breakdown request. Setting both here in one
  // handler means React batches them into a single update, so `range` and
  // `selectedPeriod` are never inconsistent with each other in any render.
  const handleRangeChange = (newRange: UsageRangeKey) => {
    setRange(newRange);
    setSelectedPeriod(undefined);
  };

  // Computed once per render and threaded into both useFetchCreditUsage
  // calls below plus the clamp line's own usageRangeFor call, instead of
  // each of those three calling `new Date()` independently: right around
  // the UTC midnight boundary, independent calls could otherwise land on
  // different sides of it and derive different [from, to) windows for the
  // same `range` within one render, so chart/breakdown and the clamp would
  // disagree about what the selected range actually covers.
  const now = new Date();
  const chart = useFetchCreditUsage({ range, now, deploymentId, component: component || undefined });
  // Clamped to the selected range's own window - see clampPeriodToRange for
  // why: on the YEAR_1 range the first/last clicked bucket would otherwise
  // request a whole calendar month even though only part of it is in range.
  const period = selectedPeriod ? clampPeriodToRange(periodBoundsFor(selectedPeriod, chart.granularity), usageRangeFor(range, now)) : undefined;
  // GRANULARITY_TOTAL: the breakdown only needs one aggregate bucket per
  // component, not one per component per period - it does not fetch a time
  // series at all. When a period is selected, `period` narrows [from, to)
  // down to that single clicked bucket, so this still returns one bucket per
  // component, just scoped to that bucket instead of the whole range.
  const breakdown = useFetchCreditUsage({
    range,
    now,
    deploymentId,
    component: component || undefined,
    groupByComponent: true,
    granularity: Granularity.GRANULARITY_TOTAL,
    period,
  });

  return (
    <Box>
      <Heading size="md">Usage</Heading>
      <Text paddingTop="8px">Credits consumed by this deployment.</Text>

      <Flex align="center" justify="space-between" wrap="wrap" gap="16px">
        <CreditUsageRangeSelector value={range} onChange={handleRangeChange} loading={chart.isLoading} />
        <UsageComponentFilter value={component} onChange={setComponent} />
      </Flex>

      {/* One combined alert for either call failing: this page already shows a
          single top-level alert, and a breakdown failure is just as much a
          reason to distrust the page as a chart failure - splitting it into
          two alerts (one per section) would add a second alert component for
          no benefit, since the message is the same either way. What matters
          is that a breakdown failure can never look like "no component data"
          - see the empty-buckets fallback below. */}
      {!!(chart.error || breakdown.error) && (
        <Alert status="error">
          <AlertIcon />
          Unable to fetch usage data
        </Alert>
      )}

      {chart.isLoading ? (
        <SectionSpinner />
      ) : (
        // Suppress the chart (and its "No usage recorded yet" empty state)
        // when the call errored: chart.data is undefined at that point, so
        // buckets would default to [] and the empty state would assert
        // something false about the customer's usage right below the error
        // alert above.
        !chart.error && <CreditUsageChart buckets={chart.data?.buckets || []} granularity={chart.granularity} onPeriodClick={setSelectedPeriod} />
      )}

      <Text fontSize="sm" color="gray.600" paddingTop="8px">
        Usage is finalised daily; data through {lastCompleteDay(now)} (UTC). Today&apos;s usage appears tomorrow.
      </Text>

      <Flex align="center" justify="space-between" paddingTop="32px" paddingBottom="8px">
        <Heading size="sm">Usage by component{selectedPeriod ? ` — ${selectedPeriod}` : ""}</Heading>
        {selectedPeriod && (
          <Button size="xs" variant="link" onClick={() => setSelectedPeriod(undefined)}>
            Clear, show whole range
          </Button>
        )}
      </Flex>
      {!breakdown.isLoading &&
        // Suppress the breakdown (and its "No usage recorded for this
        // period." empty state) when the call errored: breakdown.data is
        // undefined at that point, so buckets/totalCredits would default to
        // [] / 0 and the empty state would assert something false about the
        // customer's usage right below the error alert above - same
        // rationale as suppressing the chart on chart.error.
        !breakdown.error && <CreditUsageBreakdown buckets={breakdown.data?.buckets || []} totalCredits={breakdown.data?.total_credits || 0} hideHeading />}
    </Box>
  );
};
